import styled, {css} from 'styled-components';
import {media} from '../../styles/components/media-queries';

export const Sheet = styled.div`
  width: 58rem;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background-color: ${p => p.theme.tertiaryBgColor};
  border-radius: 1.6rem;
  overflow: hidden;
  color: ${p => p.theme.quinaryTextColor};
  ${media.small`
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    max-height: 92vh;
    border-radius: 2.4rem 2.4rem 0 0;
  `};
`;

export const Header = styled.div`
  position: relative;
  padding: 5.6rem 3rem 2rem;
  ${media.small`
    padding: 6rem 2rem 2rem;
  `};
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 2rem;
  left: 2.4rem;
  padding: 0.4rem;
  border: 0;
  background: none;
  cursor: pointer;
  color: ${p => p.theme.quinaryTextColor};
  line-height: 0;
  ${media.small`
    left: 1.6rem;
  `};
`;

export const Title = styled.h2`
  margin: 0 0 0.8rem;
  font-size: 2.6rem;
  line-height: 3.2rem;
  font-weight: 700;
  color: ${p => p.theme.quinaryTextColor};
`;

export const Subtitle = styled.p`
  margin: 0;
  font-size: 1.5rem;
  line-height: 2.2rem;
  color: ${p => p.theme.tertiaryTextColor};
`;

export const List = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 1.6rem 2.4rem;
  background-color: ${p => p.theme.secondaryBgColor};
  ${media.small`
    padding: 1.6rem;
  `};
`;

export const Notice = styled.div`
  margin-bottom: 1.2rem;
  padding: 1.2rem 1.4rem;
  border-radius: 0.8rem;
  font-size: 1.4rem;
  line-height: 2rem;
  background-color: ${p => p.theme.errorBgColor};
  color: ${p => p.theme.quinaryTextColor};
`;

export const Card = styled.div`
  margin-bottom: 1.2rem;
  padding: 1.4rem;
  border: 0.1rem solid ${p => p.theme.borderColor};
  border-radius: 0.8rem;
  background-color: ${p => p.theme.tertiaryBgColor};
`;

export const ProductRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.2rem;
`;

export const Thumb = styled.div`
  flex: 0 0 5.6rem;
  width: 5.6rem;
  height: 5.6rem;
  border: 0.1rem solid ${p => p.theme.borderColor};
  border-radius: 0.6rem;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 100%;
  }
`;

export const ItemCode = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${p => p.theme.tertiaryTextColor};
`;

export const ItemName = styled.div`
  font-size: 1.5rem;
  line-height: 2rem;
  color: ${p => p.theme.quinaryTextColor};
`;

export const RatingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1.2rem;
`;

export const Stars = styled.div`
  display: flex;
  gap: 0.6rem;
`;

export const StarButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  line-height: 0;
  color: ${p =>
    p.$filled ? p.theme.ratingColor : p.theme.quaternaryTextColor};

  &:disabled {
    cursor: default;
  }
`;

export const CommentButton = styled.button`
  position: relative;
  width: 4.2rem;
  height: 4.2rem;
  border: 0.1rem solid ${p => p.theme.primaryColor};
  border-radius: 0.8rem;
  background-color: ${p =>
    p.$open ? p.theme.secondaryLightHover : p.theme.tertiaryBgColor};
  color: ${p => p.theme.primaryColor};
  cursor: pointer;
  line-height: 0;

  &:disabled {
    cursor: default;
  }
`;

export const CommentBadge = styled.span`
  position: absolute;
  top: -0.7rem;
  right: -0.7rem;
  width: 1.8rem;
  height: 1.8rem;
  border-radius: 0.4rem;
  background-color: ${p => p.theme.primaryColor};
  color: ${p => p.theme.primaryTextColor};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ItemError = styled.div`
  margin-top: 1rem;
  font-size: 1.4rem;
  line-height: 2rem;
  color: ${p => p.theme.tertiaryColor};
`;

export const CommentBox = styled.textarea`
  width: 100%;
  min-height: 7.6rem;
  margin-top: 1.2rem;
  padding: 1rem 1.2rem;
  border: 0.1rem solid ${p => p.theme.borderDarkerColor};
  border-radius: 0.8rem;
  font-family: inherit;
  font-size: 1.5rem;
  color: ${p => p.theme.quinaryTextColor};
  resize: vertical;
  box-sizing: border-box;
  outline: none;

  &::placeholder {
    color: ${p => p.theme.tertiaryTextColor};
  }

  &:focus {
    border-color: ${p => p.theme.primaryColor};
  }
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
  padding: 1.4rem 2.4rem;
  border-top: 0.1rem solid ${p => p.theme.borderColor};
  ${media.small`
    flex-direction: column;
    gap: 1rem;
    padding: 1.4rem 1.6rem 2rem;
  `};
`;

export const RatedCount = styled.div`
  flex: 1;
  font-size: 1.4rem;
  color: ${p => p.theme.tertiaryTextColor};
  ${media.small`
    order: 1;
  `};
`;

const buttonBase = css`
  height: 4.6rem;
  padding: 0 2.4rem;
  border: 0;
  border-radius: 0.6rem;
  font-family: inherit;
  font-size: 1.6rem;
  font-weight: 700;
  cursor: pointer;
`;

export const LaterButton = styled.button`
  ${buttonBase};
  background: none;
  font-weight: 600;
  color: ${p => p.theme.quinaryTextColor};
  ${media.small`
    order: 3;
    height: 3.6rem;
  `};
`;

export const SubmitButton = styled.button`
  ${buttonBase};
  background-color: ${p => p.theme.primaryColor};
  color: ${p => p.theme.primaryTextColor};

  &:hover:enabled {
    background-color: ${p => p.theme.primaryColorHover};
  }

  &:disabled {
    background-color: ${p => p.theme.disabledColor};
    cursor: default;
  }
  ${media.small`
    order: 2;
    width: 100%;
  `};
`;

export const ThankYou = styled.div`
  padding: 0 3rem 3rem;
  ${media.small`
    padding: 0 2rem 3rem;
  `};

  ${SubmitButton} {
    margin-top: 2.4rem;
  }
`;
